import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import type { ExtraProductSelectionInput, TripType } from '@eti/schema-types';
import {
  MULTI_STOP,
  OPEN_JAW_DOUBLE,
  OPEN_JAW_SINGLE_DESTINATION,
  OPEN_JAW_SINGLE_ORIGIN,
} from '../../../../../constants/tripTypesConstants';
import Loading from '../../../../../common/loading/components/Loading';
import AddOnNotAvailable from '../components/AddOnNotAvailable';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { getAddedAncillaryBundle } from '../utils/ancillaryBundle';
import ErrorPage from '../../../../status-pages/components/ErrorPage';
import type { OrderTypePostbooking } from '../../utils/OrderUtils';
import ExtraProductsPageContainer from './ExtraProductsPageContainer';
import { ORDER_QUERY } from './queries';

import { ADD_TO_ADDON_CART, EMPTY_ADDON_CART_MUTATION } from './mutations';

const isMultiStop = (type?: TripType) =>
  [OPEN_JAW_SINGLE_ORIGIN, OPEN_JAW_SINGLE_DESTINATION, OPEN_JAW_DOUBLE, MULTI_STOP].includes(
    type ?? '',
  );

const ExtraProductsGraphqlComponent = () => {
  const [skipQuery, setSkipQuery] = useState(true);
  const { data, loading, error } = useQuery(ORDER_QUERY, {
    fetchPolicy: 'no-cache',
    skip: skipQuery,
  });
  const [addToAddonCart, { loading: addToAddonCartLoading }] = useMutation(ADD_TO_ADDON_CART);
  const [emptyCart, { loading: emptyCartLoading }] = useMutation(EMPTY_ADDON_CART_MUTATION, {
    onCompleted: () => {
      setSkipQuery(false);
    },
  });
  const history = useHistory();

  useEffect(() => {
    emptyCart();
  }, [emptyCart]);

  if (skipQuery || loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorPage />;
  }

  const addedAncillaryBundles = getAddedAncillaryBundle(data);
  const contactEmail = data?.orderNew?.customerDetails?.email ?? null;
  const trips = data?.orderNew?.trips ?? [];
  const selectedTrip = trips[0];
  const addOnOrders = data?.orderNew?.addOnOrders ?? [];
  const addOnOrderProducts =
    addOnOrders?.flatMap((productOrder) => productOrder?.extraProducts) || [];

  if (trips.length > 1) {
    return <AddOnNotAvailable />;
  }

  const extraProductsInTrip = selectedTrip?.extraProducts || [];

  const onAddToAddonCart = async (selection: ExtraProductSelectionInput[]) => {
    try {
      await emptyCart();

      await addToAddonCart({
        variables: {
          tripId: selectedTrip?.tripId || '',
          extraProductSelection: selection,
        },
      });

      history.push(routes.PAYMENT_LINK);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  };

  const order = {
    availableExtraProducts: selectedTrip?.availableExtraProducts?.map((availableExtraProduct) => ({
      ...availableExtraProduct,
      id: Number(availableExtraProduct?.id),
    })),
    bounds: selectedTrip?.bounds || [],
    extraProducts: [
      ...extraProductsInTrip.map((extraProduct) => ({
        ...extraProduct,
        id: Number(extraProduct?.id),
      })),
      ...addOnOrderProducts.map((product) => ({ ...product, id: Number(product?.id) })),
    ],
    travelers: selectedTrip?.travelers,
    tripId: selectedTrip?.tripId,
    isMultiStop: isMultiStop(selectedTrip?.type),
    addedAncillaryBundles,
  } as unknown as OrderTypePostbooking;

  return (
    <ExtraProductsPageContainer
      contactEmail={contactEmail || ''}
      isAddToAddonCartLoading={addToAddonCartLoading || emptyCartLoading}
      onAddToAddonCart={onAddToAddonCart}
      order={order}
    />
  );
};

export default ExtraProductsGraphqlComponent;
